import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useUser } from "../state";
import { VoteJSON } from "../models/vote";
import { api } from "../api";
import { JudgeList } from "../DesignSystem/JudgeList";
import { CompoCategoriesClient } from "../../../src/entity/CompoCategoriesClient";
import { ChipsAPI } from "../../../src/apidefs";
import { Compo } from "../models/compos";
import { EntryJSON } from "../models/entries";
import { Heading } from "../DesignSystem/formatting/Heading";
import { Subheading } from "../DesignSystem/formatting/Subheading";
import { Countdown } from "../DesignSystem/Countdown";
import Tooltip from "../DesignSystem/Tooltip";
import { Util } from "../util";
import { RLink } from "../DesignSystem/RLink";

type JudgeParams = {
  id: string;
};

export type JudgingProgress = {
  entry: EntryJSON;
  voteRequired: boolean;
  vote: VoteJSON;
  reviewBounty: number;
}[];

export const JudgePage = (): JSX.Element => {
  const { id } = useParams<JudgeParams>();
  const userState = useUser();
  const [apiResponse, setApiResponse] = useState<
    ChipsAPI["/api/compo/:id/judge"]["GET"]["response"] | undefined
  >();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await api.get<"/api/compo/:id/judge">(
        `/api/compo/${id}/judge`
      );
      if (!api.isToast(result) && !cancelled) {
        setApiResponse(result);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const user = userState.type === "logged-in" ? userState.user : undefined;
  if (!apiResponse || api.isToast(apiResponse) || !user) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-6">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-800">
          <Heading>Loading super judging panel.</Heading>
          <Subheading>Please hold while splines are reticulated.</Subheading>
        </div>
      </div>
    );
  }
  const { judgingProgress, compo, categories } = apiResponse;

  const entriesVoted = judgingProgress.filter((x) => x.vote).length;
  const requiredEntries = judgingProgress.filter((x) => x.voteRequired).length;
  const percentage = requiredEntries > 0
    ? Math.floor((100 * entriesVoted) / requiredEntries)
    : 0;

  let message = "";
  if (percentage === 0) {
    message = "It's a beautiful day to start judging!";
  } else if (percentage < 25) {
    message =
      "They say that some see the glass half-full, and some see it half-empty. I dunno about the glass, but your judging looks pretty half-empty to me!";
  } else if (percentage < 50) {
    message = "Working on up through the pile!";
  } else if (percentage < 75) {
    message = "Half way!";
  } else if (percentage < 100) {
    message = "Almost done!";
  } else if (percentage === 100) {
    message = "All done judging! Nice job!";
  } else if (percentage > 100) {
    message = "Above and beyond! Go for the gold!";
  }

  if (requiredEntries === 0) {
    message = "";
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      {/* Header Card */}
      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-800">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          Voting on <span className="text-chips-gold">{compo.name}</span>
        </h1>
        <RLink to={`/compo/${id}`} variant="secondary">
          See full compo details
        </RLink>

        <div className="mt-4 space-y-3 text-slate-600 dark:text-slate-300">
          {requiredEntries === 0 ? (
            <p>Feel free to vote on these songs for extra gold!</p>
          ) : (
            <div>
              <Tooltip
                element={
                  <p>
                    You've voted on <span className="font-semibold text-chips-gold">{percentage}%</span> of your required entries —{" "}
                    <span className="font-semibold">{entriesVoted}</span> of <span className="font-semibold">{requiredEntries}</span>.
                  </p>
                }
                tooltip={<div>{message}</div>}
              />
            </div>
          )}

          <p className="text-sm">
            Writing reviews will cause reviews on your song to become worth
            more. So if you leave reviews, you'll get reviews!
          </p>
        </div>

      </div>

      {/* Song List */}
      <div className="mb-6">
        <JudgeList
          judgingProgress={judgingProgress}
          categories={categories.map(
            (category) => new CompoCategoriesClient(category)
          )}
          compo={new Compo(compo)}
          user={user}
          onVote={(newVote) => {
            setApiResponse({
              ...apiResponse,
              judgingProgress: judgingProgress.map((item) =>
                item.entry.id === newVote.entry_id
                  ? { ...item, vote: newVote }
                  : item
              ),
            });
          }}
        />
      </div>

      {/* Countdown Card */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-800">
        <div className="text-center">
          <Countdown
            until={new Date(compo.judging_stop)}
            text="left to judge!"
          />
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            That will be at {Util.FormatDate(new Date(compo.judging_stop))}
          </p>
        </div>
      </div>
    </div>
  );
};
